import { FC, useState } from "react";
import {
  Button,
  Label,
  LoadingButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Spinner,
  toast,
  Typography,
} from "@altimateai/lego";
import { IconAlertCircle, IconBrandSlack, IconHash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { shareToSlack } from "@/helpers/apis";
import { useSlackChannels } from "@/pages/alerts/useSlackChannels";

interface ShareSlackTabProps {
  sessionId: string;
  open: boolean;
  onSuccess?: () => void;
}

const SLACK_CHANNEL_FIELD_ID = "share-slack-channel";
const SLACK_CHANNEL_LABEL = "Slack channel";

const ShareSlackTab: FC<ShareSlackTabProps> = ({ sessionId, open, onSuccess }) => {
  const [selectedChannel, setSelectedChannel] = useState("");
  const {
    slackChannels: webhookChannels,
    isLoading: isLoadingWebhooks,
    error: webhooksError,
  } = useSlackChannels(open);

  const { mutate: sendToSlack, isPending } = useMutation({
    mutationFn: () => shareToSlack({ session_id: sessionId, webhook_id: Number(selectedChannel) }),
    onSuccess: () => {
      toast({ title: "Shared to Slack" });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to share to Slack",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoadingWebhooks) {
    return (
      <Stack
        align="center"
        justify="center"
        className="al-py-8"
        role="status"
        aria-live="polite"
        aria-label="Loading Slack channels"
      >
        <Spinner />
      </Stack>
    );
  }

  // Webhook fetch failed — surface the error instead of the "no webhooks"
  // empty state, which would incorrectly suggest the user hasn't configured
  // any yet.
  if (webhooksError) {
    return (
      <Stack direction="column" align="center" gap="md" className="al-py-6">
        <IconAlertCircle className="al-h-10 al-w-10 al-text-destructive" />
        <Typography variant="body" size="sm" className="al-text-center al-text-muted-foreground">
          Couldn’t load Slack webhooks. Please try again in a moment.
        </Typography>
      </Stack>
    );
  }

  // No webhooks configured — guide user to Notifications
  if (!webhookChannels || webhookChannels.length === 0) {
    return (
      <Stack direction="column" align="center" gap="md" className="al-py-6">
        <IconBrandSlack className="al-h-10 al-w-10 al-text-muted-foreground" />
        <Typography variant="body" size="sm" className="al-text-center al-text-muted-foreground">
          No Slack webhooks configured. Add a Slack webhook in Notifications to share sessions to
          Slack.
        </Typography>
        <Button variant="outline" asChild>
          <Link to="/settings/notification">Go to Notifications</Link>
        </Button>
      </Stack>
    );
  }

  // Webhook channels available — show picker
  return (
    <Stack direction="column" gap="md">
      <div>
        <Label htmlFor={SLACK_CHANNEL_FIELD_ID} className="al-mb-2 al-block">
          Slack Channel
        </Label>
        <Select value={selectedChannel} onValueChange={setSelectedChannel}>
          <SelectTrigger id={SLACK_CHANNEL_FIELD_ID} aria-label={SLACK_CHANNEL_LABEL}>
            <SelectValue placeholder="Select a webhook channel" />
          </SelectTrigger>
          <SelectContent>
            {webhookChannels.map(ch => (
              <SelectItem key={ch.id} value={String(ch.id)}>
                <Stack direction="row" align="center" gap="xs">
                  <IconHash className="al-h-4 al-w-4" />
                  {ch.config?.name}
                </Stack>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Typography variant="body" size="xs" className="al-text-muted-foreground">
        An AI-generated summary of this conversation will be posted to the selected channel via
        webhook.
      </Typography>
      <Stack direction="row" justify="end" gap="sm">
        <LoadingButton
          loading={isPending}
          onClick={() => sendToSlack()}
          disabled={!selectedChannel}
        >
          <IconBrandSlack className="al-h-4 al-w-4 al-mr-1" />
          Send to Slack
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

export default ShareSlackTab;
